import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';
import { Columns, Lock, Eye, EyeOff, Settings2, GripVertical, RotateCcw } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * ColumnSelector - Componente para que usuarios personalicen columnas visibles
 * 
 * Props:
 * - section: string - Nombre de la sección (alerts, movimientos, padron, etc.)
 * - vista: string - Vista: 'lista' o 'detalle' (default: 'lista')
 * - onColumnsChange: (columns: Array<{key, label, visible}>) => void - Callback con columnas actualizadas
 * - trigger: React.ReactNode - Elemento que activa el popover (opcional)
 * - defaultColumns: Array<{key, label}> - Columnas por defecto si no hay config en backend
 */
const ColumnSelector = ({ section, vista = 'lista', onColumnsChange, trigger, defaultColumns = [] }) => {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localHidden, setLocalHidden] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Cargar configuración cuando se abre el popover
  useEffect(() => {
    if (open && section) {
      loadFieldConfig();
    }
  }, [open, section, vista]);

  // Notificar cambios iniciales
  useEffect(() => {
    if (fields.length > 0 && onColumnsChange) {
      const visibleFields = fields
        .filter(f => f.visible && !localHidden.has(f.campo))
        .map(f => ({ key: f.campo, label: f.label, visible: true }));
      onColumnsChange(visibleFields);
    }
  }, [fields]);

  const loadFieldConfig = async () => {
    try {
      setLoading(true);
      
      // Obtener configuración para la sección y vista
      const response = await api.get(`/field-config/section/${section}?vista=${vista}`);
      const backendFields = response.data?.campos || [];
      
      if (backendFields.length > 0) {
        setFields(backendFields);
        // Cargar campos ocultos del usuario
        const hiddenFields = backendFields.filter(f => !f.visible).map(f => f.campo);
        setLocalHidden(new Set(hiddenFields));
      } else if (defaultColumns.length > 0) {
        // Usar columnas por defecto
        const defaultFields = defaultColumns.map((col, idx) => ({
          campo: col.key,
          label: col.label,
          visible: true,
          bloqueado: false,
          orden: idx + 1
        }));
        setFields(defaultFields);
      }
    } catch (error) {
      console.error('Error loading field config:', error);
      // Si falla, usar columnas por defecto
      if (defaultColumns.length > 0) {
        const defaultFields = defaultColumns.map((col, idx) => ({
          campo: col.key,
          label: col.label,
          visible: true,
          bloqueado: false,
          orden: idx + 1
        }));
        setFields(defaultFields);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleField = (campo) => {
    const field = fields.find(f => f.campo === campo);
    if (field?.bloqueado) {
      toast.error('Este campo no puede ocultarse');
      return;
    }

    setLocalHidden(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campo)) {
        newSet.delete(campo);
      } else {
        newSet.add(campo);
      }
      return newSet;
    });
    setHasChanges(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = [...fields];
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    // Actualizar orden
    reordered.forEach((field, idx) => {
      field.orden = idx + 1;
    });

    setFields(reordered);
    setHasChanges(true);
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      // Guardar preferencias del usuario
      const camposOcultos = Array.from(localHidden);
      const ordenPersonalizado = fields.map(f => f.campo);
      
      await api.put(`/field-config/user/${section}`, null, {
        params: {
          campos_ocultos: camposOcultos,
          orden_personalizado: ordenPersonalizado
        }
      });
      
      // Notificar al componente padre
      if (onColumnsChange) {
        const visibleFields = fields
          .filter(f => !localHidden.has(f.campo))
          .map(f => ({ key: f.campo, label: f.label, visible: true }));
        onColumnsChange(visibleFields);
      }
      
      toast.success('Preferencias guardadas');
      setHasChanges(false);
      setOpen(false);
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setLocalHidden(new Set());
    // Restaurar orden original
    const sorted = [...fields].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    setFields(sorted);
    setHasChanges(true);
  };

  const isFieldVisible = (campo) => !localHidden.has(campo);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid="column-selector-btn">
            <Columns size={16} className="mr-2" />
            Columnas
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" style={{ backgroundColor: '#fff' }}>
        <div className="p-3 border-b bg-slate-50">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Settings2 size={16} />
            Personalizar Columnas
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            Arrastra para reordenar, click para mostrar/ocultar
          </p>
        </div>
        
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="columns">
              {(provided) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps}
                  className="max-h-64 overflow-y-auto p-2"
                >
                  {fields.map((field, index) => (
                    <Draggable key={field.campo} draggableId={field.campo} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-2 p-2 rounded ${
                            snapshot.isDragging ? 'bg-blue-50 shadow-md' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div {...provided.dragHandleProps} className="cursor-grab">
                            <GripVertical size={14} className="text-slate-400" />
                          </div>
                          
                          <button
                            onClick={() => handleToggleField(field.campo)}
                            disabled={field.bloqueado}
                            className={`flex-1 flex items-center gap-2 text-left text-sm ${
                              field.bloqueado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                            }`}
                          >
                            {isFieldVisible(field.campo) ? (
                              <Eye size={14} className="text-green-600 flex-shrink-0" />
                            ) : (
                              <EyeOff size={14} className="text-slate-400 flex-shrink-0" />
                            )}
                            <span className={`truncate ${!isFieldVisible(field.campo) ? 'text-slate-400 line-through' : ''}`}>
                              {field.label}
                            </span>
                          </button>
                          
                          {field.bloqueado && (
                            <Lock size={12} className="text-amber-500 flex-shrink-0" title="Campo obligatorio" />
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        
        <div className="p-3 border-t flex justify-between bg-slate-50">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleReset}
            disabled={!hasChanges}
          >
            <RotateCcw size={14} className="mr-1" />
            Restablecer
          </Button>
          <Button 
            size="sm" 
            onClick={handleApply} 
            disabled={saving || !hasChanges} 
            style={{ backgroundColor: '#2563eb', color: '#fff' }}
          >
            {saving ? 'Guardando...' : 'Aplicar'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnSelector;
